import { KursyGeneralList, KursPkpir } from "@/kursy/general";
import { Link, useNavigate } from "@tanstack/react-router";
import { CourseTitleCard } from "./CourseTitleCard";
import { useAuth } from "@/context/AuthContext";
import { getSubscriptionCourse } from "@/api/api";
import type { CourseKey } from "@/api/api.types";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { CourseAccessDrawer } from "@/components/courses/CourseAccessDrawer";
import { useCourseAccessDrawer } from "@/components/courses/useCourseAccessDrawer";

export const CoursesGeneralList = (): JSX.Element => {
	const { isAuthenticated } = useAuth();
	const navigate = useNavigate();
	const { state, dispatch, openAcademiaWebsite } = useCourseAccessDrawer("https://akademialtca.pl/");
	const [selectedCourseKey, setSelectedCourseKey] = useState<CourseKey | null>(null);

	const { data: subscriptionData } = useQuery({
		queryKey: ["subscriptionCourse", selectedCourseKey],
		queryFn: () => getSubscriptionCourse(selectedCourseKey!),
		enabled: isAuthenticated && selectedCourseKey !== null,
	});

	const handleCourseClick = (kurs: { slug: string; title: string; description: string }): void => {
		if (!isAuthenticated) {
			dispatch({
				type: "OPEN_DRAWER",
				payload: { title: kurs.title, description: kurs.description },
			});
		} else {
			setSelectedCourseKey(kurs.slug as CourseKey);
		}
	};

	useEffect((): (() => void) => {
		if (subscriptionData == null || selectedCourseKey == null) return () => undefined;

		let cancelled = false;

		const run = async (): Promise<void> => {
			if (subscriptionData.has_access) {
				await navigate({
					to: "/course/$courseKey",
					params: { courseKey: selectedCourseKey },
				});
				if (!cancelled) setSelectedCourseKey(null);
			} else {
				dispatch({
					type: "OPEN_DRAWER",
					payload: { title: KursPkpir.title, description: KursPkpir.description },
				});
				dispatch({ type: "SET_STEP", payload: "go_to_website" });
				if (!cancelled) setSelectedCourseKey(null);
			}
		};

		run().catch((error) => {
			console.error("Error handling subscription response:", error);
		});

		return () => {
			cancelled = true;
		};
	}, [subscriptionData, selectedCourseKey, navigate, dispatch]);

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			<div className="flex flex-col gap-3">
				{KursyGeneralList.map((kurs) => (
					<Link key={kurs.slug} to={`/courses/${kurs.slug}` as "/courses/pop"}>
						<CourseTitleCard kurs={kurs} />
					</Link>
				))}

				<button
					type="button"
					className="w-full text-left"
					onClick={() => {
						handleCourseClick(KursPkpir);
					}}
				>
					<CourseTitleCard kurs={KursPkpir} />
				</button>
			</div>

			<CourseAccessDrawer
				state={state}
				onOpenChange={(open) => {
					if (!open) {
						dispatch({ type: "CLOSE_DRAWER" });
					}
				}}
				onCloseDrawer={() => {
					dispatch({ type: "CLOSE_DRAWER" });
				}}
				onOpenWebsite={openAcademiaWebsite}
			/>
		</main>
	);
};
